import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const CATEGORIES = ["全部", "新闻", "专题", "影评"];

export default function NewsPage() {
  const [category, setCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");

  useSeo({
    title: "影视资讯 - 飘花电影网",
    description:
      "飘花电影网影视资讯频道，涵盖最新影视新闻、专题报道与影评，按时间倒序更新，帮助影视爱好者掌握行业动态与深度解读。",
    keywords: "影视资讯,影视新闻,专题报道,影评,飘花电影网",
  });

  const result = useMemo(() => {
    const kw = keyword.trim();
    return [...newsList]
      .filter((item) => (category === "全部" ? true : item.category === category))
      .filter((item) => (kw ? item.title.includes(kw) || item.summary.includes(kw) : true))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [category, keyword]);

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: "资讯" }]} />

        <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-4 md:p-6">
          <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-primary-950">
              <i className="ri-newspaper-line" />
            </span>
            影视资讯
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            最新影视新闻、专题报道与深度影评，按时间倒序呈现。
          </p>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                    category === item
                      ? "bg-primary-600 font-medium text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-64">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索资讯关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>
        </div>

        {result.length > 0 ? (
          <div className="mt-4 space-y-4">
            {result.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-300 md:flex-row md:p-4"
              >
                <div className="h-40 w-full overflow-hidden rounded-lg bg-background-200 md:h-32 md:w-52 md:shrink-0">
                  <img
                    src={item.cover}
                    alt={`${item.title}配图`}
                    title={`${item.title} - 影视资讯`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col">
                  <div className="flex items-center gap-2 text-xs text-foreground-400">
                    <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-700">{item.category}</span>
                    <span>{item.date}</span>
                    <span className="text-background-400">·</span>
                    <span>{item.author}</span>
                  </div>
                  <h2 className="mt-2 font-heading text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
                    {item.title}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">{item.summary}</p>
                  <span className="mt-auto flex items-center gap-1 pt-3 text-sm text-primary-600">
                    阅读全文
                    <i className="ri-arrow-right-s-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <p className="mt-4 font-medium text-foreground-700">没有找到相关资讯</p>
            <p className="mt-1 text-sm text-foreground-400">换个关键词或切换分类试试</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setCategory("全部");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              重置筛选
            </button>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}